import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { PalantirButtonComponent, PalantirSkeletonComponent } from '@fieldcontrol/palantir'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { MAINTENANCE_STATUS_COLORS, MAINTENANCE_STATUS_COLORS_OPACITY_10 } from 'projects/fce-components/src/app/core/constants/maintenance-status-colors.constant'
import { DetailPageOptions, DetailPageResult } from './detail-page.types'
import { PipesModule } from '../../../../../../core/pipes/pipes.module'
import { DetailsSectionService } from '../../details-section.service'
import { WarrantyStatusLabelComponent } from '../warranty-status-label/warranty-status-label.component'

@Component({
    selector: 'app-detail-page',
    templateUrl: './detail-page.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        MatIconModule,
        TranslateModule,
        NgScrollbarModule,
        PipesModule,
        PalantirButtonComponent,
        PalantirSkeletonComponent,
        WarrantyStatusLabelComponent
    ]
})
export class DetailPageComponent implements OnInit {
  @Output()
  private readonly closePage = new EventEmitter<void>()

  @Input({ required: true })
  public options!: DetailPageOptions | null
  public isHovering = signal<boolean>(false)

  public limit = signal<number>(10)
  public offset = signal<number>(0)
  public paginationLimit = signal<number>(10)

  public isHoveringIndex = signal<number>(-1)
  public isLoading = signal<boolean>(true)
  public hasNextPage = signal<boolean>(false)
  public hasLastPage = signal<boolean>(false)
  public hasFirstPage = signal<boolean>(false)
  public hasPreviousPage = signal<boolean>(false)
  public result = signal<DetailPageResult | null>(null)
  public readonly maintenanceStatusColors = MAINTENANCE_STATUS_COLORS as any
  public readonly maintenanceStatusColorsOpacity10 = MAINTENANCE_STATUS_COLORS_OPACITY_10 as any

  private readonly detailsSectionService = inject(DetailsSectionService)

  public async ngOnInit(): Promise<void> {
    await this.load()
  }

  public trackByFn(index: number): number {
    return index
  }

  public close(): void {
    this.closePage.emit()
  }

  private async load(): Promise<void> {
    this.isLoading.set(true)

    this.options?.service?.limit.set(this.limit())
    this.options?.service?.offset.set(this.offset())

    const response = await this.options?.service?.searchPage()
    const data = response?.data[`${this.options?.type}s`]

    this.result.set(data)
    this.hasNextPage.set(data.pageInfo.hasNextPage)
    this.hasLastPage.set(data.pageInfo.hasNextPage)
    this.hasFirstPage.set(data.pageInfo.hasPreviousPage)
    this.hasPreviousPage.set(data.pageInfo.hasPreviousPage)

    const totalCount = this.result()?.totalCount || 0
    const paginationLimit = this.offset() + this.limit() <= totalCount ? this.offset() + this.limit() : totalCount
    this.paginationLimit.set(paginationLimit)

    this.isLoading.set(false)
  }

  public async emitEvent(item: any): Promise<void> {
    const type = this.options?.type === 'equipment' ? 'viewEquipment' : 'viewMaintenance'
    this.detailsSectionService.dispatch({ id: item.id, item, type, reloadPage: true })
  }

  public async toNextPage(): Promise<void> {
    this.offset.set(this.offset() + this.limit())
    await this.load()
  }

  public async toPreviousPage(): Promise<void> {
    const offset = this.offset() - this.limit()
    this.offset.set(offset > 0 ? offset : 0)
    await this.load()
  }

  public async toFirstPage(): Promise<void> {
    this.offset.set(0)
    await this.load()
  }

  public async toLastPage(): Promise<void> {
    this.offset.set(Number(this.result()?.totalCount) - this.limit())
    await this.load()
  }
}
